import React, { useCallback, Fragment } from 'react';
// eslint-disable-next-line jira/react-magnetic-di/no-unneeded
import { di } from 'react-magnetic-di';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import {
	type AnalyticsHandlerArgumentsOld,
	type CommandPaletteModalProps,
	CommandPalette,
	type AnalyticsAttributes,
	useCommandPaletteIsOpen,
} from '@atlassian/jira-command-palette';

import { handleAnalytics } from '@atlassian/jira-command-palette-common';
import { useQuickSearchCommand } from '@atlassian/jira-command-palette-quick-search';
import { useCommandPaletteRegistry } from '@atlassian/jira-command-palette-registry';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useIntl } from '@atlassian/jira-intl';
import {
	type EventTypes,
	fireUIAnalytics,
	fireTrackAnalytics,
	fireOperationalAnalytics,
	OPERATIONAL_EVENT_TYPE,
	UI_EVENT_TYPE,
	TRACK_EVENT_TYPE,
} from '@atlassian/jira-product-analytics-bridge';
import { CONTAINER_TEST_ID } from '../common/constants';
import { useCloseDotMenu } from '../controllers/use-close-dot-menu';
import { useDisableCompalIfEmbed } from '../controllers/use-disable-compal-embed';
import { useRegisterDismissCommand } from '../controllers/use-register-dismiss-command';
import { useRegisterSections } from '../controllers/use-register-sections';
import { FooterFeedbackContent } from './footer-feedback-content';
import { useRegisterInitialCommands } from './initial-commands';
import { messages } from './messages';
import { RegisterDynamicCommands } from './register-dynamic-commands';

export const CommandPaletteIntegration = (props: CommandPaletteModalProps) => {
	// eslint-disable-next-line jira/react-magnetic-di/no-unneeded
	di(ShortcutScope);
	const { formatMessage } = useIntl();
	const [{ isOpen }] = useCommandPaletteIsOpen();
	const { quickSearchCommand } = useQuickSearchCommand({ keepSearch: true });

	useCommandPaletteRegistry();

	// TODO remove when cleaning up 'compal-refactor-analytics'
	const handleAnalyticsOld = useCallback(
		(event: UIAnalyticsEvent, eventType: EventTypes, ...args: AnalyticsHandlerArgumentsOld) => {
			const eventFunctions: {
				[key in EventTypes]?:
					| typeof fireUIAnalytics
					| typeof fireTrackAnalytics
					| typeof fireOperationalAnalytics
					| undefined;
			} = {
				[UI_EVENT_TYPE]: fireUIAnalytics,
				[TRACK_EVENT_TYPE]: fireTrackAnalytics,
				[OPERATIONAL_EVENT_TYPE]: fireOperationalAnalytics,
			};

			// TODO remove __standardize after we standardize the event payload in https://hello.atlassian.net/browse/DEE-2910
			if (event.payload.__standardize) {
				eventFunctions[eventType]?.(event, ...args);
				return;
			}

			const eventActionAndSubject = `${event.payload.actionSubject} ${event.payload.action}`;
			if (args?.[0]) {
				eventFunctions[eventType]?.(
					event,
					eventActionAndSubject,
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					args?.[0] as AnalyticsAttributes,
				);
			} else {
				eventFunctions[eventType]?.(event, eventActionAndSubject);
			}
		},
		[],
	);

	const onErrorAnalytics = useCallback(
		(
			attribution: string | undefined,
			error: Error | undefined,
			id: string,
			attributes?: AnalyticsAttributes,
		) => {
			fireErrorAnalytics({
				error,
				meta: {
					id: `jira.commandPalette.${id}`,
					packageName: 'jiraCommandPaletteIntegration',
					teamName: attribution || 'deliveroo',
				},
				attributes,
			});
		},
		[],
	);

	useRegisterSections();

	useRegisterDismissCommand();

	useCloseDotMenu();

	useDisableCompalIfEmbed();

	useRegisterInitialCommands();

	const Wrapper = isOpen ? ShortcutScope : Fragment;
	return (
		<Wrapper>
			<div data-testid={CONTAINER_TEST_ID}>
				<CommandPalette
					defaultFooterContent={<FooterFeedbackContent />}
					onAnalytics={handleAnalytics}
					onAnalyticsOld={handleAnalyticsOld}
					onErrorAnalytics={onErrorAnalytics}
					defaultPlaceholder={formatMessage(messages.defaultPlaceholderWithQuickSearch, {
						searchModeIcon: '"/"',
					})}
					defaultFallback={{
						onError: {
							label: formatMessage(messages.onErrorFallback),
						},
						onNoResults: {
							label: formatMessage(messages.noResultsFound),
						},
					}}
					rootFallback={{
						label: formatMessage(messages.noResultsFound),
						fallbackCommand: quickSearchCommand,
						fallbackSection: {
							name: formatMessage(messages.recommendedSection),
						},
					}}
					{...props}
				/>
				<RegisterDynamicCommands />
			</div>
		</Wrapper>
	);
};
